import { BuiBreadcrumb } from "@btnet/blueprint-components/BuiBreadcrumb";
import { cn } from "@btnet/utilities-core";

import "./BTBreadcrumb.less";
import type {
  IBTBreadcrumbItemProps,
  IBTBreadcrumbProps,
} from "./BTBreadcrumb.types";
import { useBTBreadcrumb } from "./useBTBreadcrumb";

export type { IBTBreadcrumbItemProps, IBTBreadcrumbProps };

const BTBreadcrumbItem = ({
  testid,
  children,
  className,
  href,
  onClick,
  overlay,
  separator,
}: IBTBreadcrumbItemProps) => (
  <BuiBreadcrumb.Item
    testid={testid}
    className={cn("bt-breadcrumb-item", className)}
    // Omit href/onClick when undefined so the key doesn't exist in props.
    // Ant Design checks `'href' in props` and renders <a> when the key is present, even if the value is undefined.
    {...(href !== undefined && { href })}
    {...(onClick !== undefined && { onClick })}
    {...(overlay !== undefined && { overlay })}
    {...(separator !== undefined && { separator })}
  >
    {children}
  </BuiBreadcrumb.Item>
);

const BTBreadcrumbComponent = (props: IBTBreadcrumbProps) => {
  const { buiProps, navProps, processedChildren } = useBTBreadcrumb(props);

  return (
    <nav {...navProps}>
      <BuiBreadcrumb {...buiProps}>{processedChildren}</BuiBreadcrumb>
    </nav>
  );
};

/**
 * BTBreadcrumb component - a styled breadcrumb navigation with responsive collapse.
 * Wraps BuiBreadcrumb from @btnet/blueprint-components with a `<nav>` element for accessibility
 * and built-in responsive collapse behavior on mobile viewports.
 *
 * Accessibility is built-in:
 * - Wrapped in `<nav aria-label="Breadcrumb">` by default; override with the `aria-label` prop when the page has multiple breadcrumb navs.
 * - For the current page, add `aria-current="page"` to the element
 *
 * Responsive collapse (enabled by default, requires 3+ items):
 * - Collapses when the row no longer fits on one line, or on mobile (max-width: 768px). A detail
 *   pane can be narrow while the viewport is wide, which the breakpoint alone never catches.
 * - First and last items remain visible
 * - Collapsed items are accessible via a "..." button dropdown
 *
 * Container measurement contract — collapse is driven by observing the breadcrumb's IMMEDIATE
 * parent (see `useBTBreadcrumb`), so a parent that violates any of these silently disables it:
 * - It must track available space: a block, or a stretch flex child — never sized by its own
 *   content. (`DetailFrame.Title`'s wrapper and `BuiHeader`'s flex-column both qualify.)
 * - No dynamic-width siblings in the same row. A "crumbs left, actions right" flex row holds the
 *   parent's width constant while the breadcrumb's real budget moves, so nothing fires.
 * - Its height must be free to grow, since clearing the md breakpoint is only detected via the
 *   parent's height change. (That path settles post-paint, so a one-frame wrap flash there is
 *   expected; resize-driven probes settle pre-paint and are invisible.)
 *
 * Known non-conforming shape: `BTListLayoutHeader`'s `<div class="ListViewJobAndTitle">` is
 * `flex-direction: column` with no `flex: 1`, next to `listActions` in a `space-between` row, so
 * it is sized by its own content. Measured live: 121px wide at both a 1585px and a 685px row.
 * Collapse cannot fire for a breadcrumb passed through `BTListLayout`'s `optionalHeader`
 * (today only `ReadonlyProposalWorksheet`, which never collapsed under the old breakpoint either).
 * @param props - The component props.
 * @param props.testid - Test ID for the breadcrumb.
 * @param props.children - BTBreadcrumb.Item children.
 * @param props.separator - Separator between items. Defaults to "/".
 * @param props.className - Additional CSS class names.
 * @param props.id - Optional HTML id for the nav element.
 * @example
 * ```tsx
 * <BTBreadcrumb testid="nav-breadcrumb">
 *   <BTBreadcrumb.Item testid="home">
 *     <a href="/">Home</a>
 *   </BTBreadcrumb.Item>
 *   <BTBreadcrumb.Item testid="products">
 *     <a href="/products">Products</a>
 *   </BTBreadcrumb.Item>
 *   <BTBreadcrumb.Item testid="current">
 *     <span aria-current="page">Current Page</span>
 *   </BTBreadcrumb.Item>
 * </BTBreadcrumb>
 * ```
 */
export const BTBreadcrumb = Object.assign(BTBreadcrumbComponent, {
  /**
   * BTBreadcrumb.Item sub-component for individual breadcrumb items.
   * Wraps BuiBreadcrumb.Item from @btnet/blueprint-components.
   * @param props - The component props.
   * @param props.testid - Test ID for the breadcrumb item.
   * @param props.children - The content to render inside the breadcrumb item.
   * @param props.className - Additional CSS class names.
   * @param props.href - Link URL for the breadcrumb item.
   * @param props.onClick - Click handler for the breadcrumb item.
   */
  Item: BTBreadcrumbItem,
});
